import { formatNumber } from '@/components/live';

/**
 * Areas in both units (owner, 2026-09-21: "polygon: provide units in hac and sqmt").
 * Hectares are the reporting unit; square metres are what a field officer or a small-plot land
 * record reads. 1 ha = 10,000 m².
 */
export const SQM_PER_HA = 10_000;

/** "3.057 ha · 30,573 m²" - three decimals at most (owner, 2026-09-27). Null-safe: an unknown area is "--", never "0 ha". */
export function formatArea(hectares?: number | string | null, haDigits = 3): string {
  if (hectares === null || hectares === undefined || hectares === '') return '--';
  const ha = Number(hectares);
  if (Number.isNaN(ha)) return '--';
  return `${formatNumber(ha, haDigits)} ha · ${formatNumber(ha * SQM_PER_HA, 0)} m²`;
}

/** Square metres alone, for a second line under a hectare figure. */
export function formatSqm(hectares?: number | string | null): string {
  if (hectares === null || hectares === undefined || hectares === '') return '--';
  const ha = Number(hectares);
  return Number.isNaN(ha) ? '--' : `${formatNumber(ha * SQM_PER_HA, 0)} m²`;
}

const EARTH_RADIUS_M = 6_378_137;

/**
 * Area of a GeoJSON ring ([lng, lat] pairs) on the sphere, in m². Used only for the live
 * readout while a boundary is being drawn - the stored figure is computed by PostGIS on save
 * and is the one that counts; the two agree to well under a percent at plot scale.
 */
export function ringAreaSqm(ring: Array<[number, number]>): number {
  if (ring.length < 3) return 0;
  const rad = Math.PI / 180;
  let total = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const [lng1, lat1] = ring[index]!;
    const [lng2, lat2] = ring[(index + 1) % ring.length]!;
    total += (lng2 - lng1) * rad * (2 + Math.sin(lat1 * rad) + Math.sin(lat2 * rad));
  }
  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

/** Area of a Polygon or MultiPolygon: outer rings minus holes. */
export function geometryAreaSqm(geometry: { type: string; coordinates: unknown } | null | undefined): number {
  if (!geometry) return 0;
  const polygons = (geometry.type === 'MultiPolygon'
    ? geometry.coordinates
    : [geometry.coordinates]) as Array<Array<Array<[number, number]>>>;
  return polygons.reduce((sum, rings) => sum + rings.reduce(
    (inner, ring, index) => inner + (index === 0 ? 1 : -1) * ringAreaSqm(ring), 0), 0);
}
